import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn } from '@angular/router';
import { MzicForgotPasswordStateService } from '@mzic/mzic-services';

/**
 * Interface for components that can be deactivated from forgot password flow
 */
export interface CanComponentDeactivate {
  canDeactivate(): boolean;
}

/**
 * Guard that clears forgot password state when user leaves the flow
 */
export const forgotPasswordFlowDeactivateGuard: CanDeactivateFn<
  CanComponentDeactivate
> = (component, currentRoute, currentState, nextState) => {
  const forgotPasswordStateService = inject(MzicForgotPasswordStateService);

  // Check if user is going to 2FA verification (allowed transition)
  const isGoingTo2FA = nextState?.url?.includes('verify-email-code');
  const isGoingToLogin = nextState?.url?.includes('/login');

  if (isGoingTo2FA) {
    return true; // Don't clear state, user is continuing the flow
  }

  if (isGoingToLogin) {
    forgotPasswordStateService.clearStateOnRouteLeave();
    return true;
  }

  // For any other navigation, clear the state
  forgotPasswordStateService.clearStateOnRouteLeave();
  return true;
};

/**
 * Guard that manages forgot password flow state on route activation
 */
export const forgotPasswordFlowActivateGuard: CanActivateFn = (
  route,
  state,
) => {
  const forgotPasswordStateService = inject(MzicForgotPasswordStateService);

  // Check if coming from an external route (not part of forgot password flow)
  const isComingFromOutsideFlow =
    !state.url.includes('forgot-password') &&
    !state.url.includes('verify-email-code');

  if (isComingFromOutsideFlow) {
    // Clear any existing forgot password state if coming from outside the flow
    forgotPasswordStateService.clearState();
  }

  return true;
};
